/**
 * Homepage API Service
 * Connects to the Talenty backend to fetch AI-generated homepage content
 */

// Import interfaces from existing homepageData.ts
import type { 
  CompanyInfo, 
  OurPromise, 
  Testimonials, 
  CandidateExperience, 
  WhatWeOffer, 
  CompanyValues, 
  // FinalCTA,
  Domain, 
  DomainsSection,
  Family, 
  Job,
  JobsSection
} from '../data/homepageData.js';
import { getCompanyFromUrl } from '../utils/company';

export interface HeroStat {
  value: string;
  label: string;
}

// Hero section interface
export interface HeroSection {
  title: string;
  subtitle: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  stats: HeroStat[];
  videoUrl?: string;
}

export interface FinalCTA {
  title: string;
  subtitle: string;
  button1Text: string;
  button2Text: string;
}

// Complete homepage content bundle interface
export interface HomepageContentBundle {
  heroSection: HeroSection;
  companyInfo: CompanyInfo;
  ourPromise: OurPromise;
  testimonials: Testimonials;
  candidateExperience: CandidateExperience;
  whatWeOffer: WhatWeOffer;
  companyValues: CompanyValues;
  finalCTA: FinalCTA;
  domainsSection: DomainsSection;
  families: Family[];
  jobsSection: JobsSection;
  generatedAt: string;
  mandantId?: string;
}

class HomepageApiService {
  private baseUrl: string;
  private companySlug: string;

  constructor() {
    // Configure API base URL via env with safe fallback to production backend
    this.baseUrl = import.meta.env.VITE_API_BASE_URL ||
      `https://ts.talenty.ai`;

    // Determine company from subdomain first, then path
    this.companySlug = getCompanyFromUrl();
  }


  /**
   * Get complete homepage content in a single API call
   * Uses the company-specific endpoint for the detected company
   */
  async getCompleteHomepageContent(): Promise<HomepageContentBundle> {
    // Use path-based company routing
    const url = `${this.baseUrl}/${this.companySlug}/homepage/complete`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Startseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get hero section content
   */
  async getHeroSection(): Promise<HeroSection> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/hero-section`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Der Startbereich konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get individual company information
   */
  async getCompanyInfo(): Promise<CompanyInfo> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/company/info`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Unternehmensinformationen konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get our promise section content
   */
  async getOurPromise(): Promise<OurPromise> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/promise`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Unser Versprechen konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get testimonials section content
   */
  async getTestimonials(): Promise<Testimonials> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/testimonials`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Erfahrungsberichte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get candidate experience section content
   */
  async getCandidateExperience(): Promise<CandidateExperience> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/candidate-experience`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Der Bewerbungsablauf konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get what we offer section content
   */
  async getWhatWeOffer(): Promise<WhatWeOffer> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/what-we-offer`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Unsere Leistungen konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get company values section content
   */
  async getCompanyValues(): Promise<CompanyValues> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/company-values`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Unternehmenswerte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get final CTA section content
   */
  async getFinalCTA(): Promise<FinalCTA> {
    const url = `${this.baseUrl}/${this.companySlug}/homepage/final-cta`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Der Abschlussbereich konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get domains content (AI-generated when available)
   */
  async getDomains(includeJobCount: boolean = true): Promise<Domain[]> {
    let url = `${this.baseUrl}/${this.companySlug}/homepage/domains`;
    
    if (!includeJobCount) {
      url += '?include_job_count=false';
    }

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Bereiche konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get families content
   */
  async getFamilies(domain?: string, includeJobCount: boolean = true): Promise<Family[]> {
    const params = new URLSearchParams();
    if (domain) params.append('domain', domain);
    if (!includeJobCount) params.append('include_job_count', 'false');

    const url = `${this.baseUrl}/${this.companySlug}/homepage/families${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Jobfamilien konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get jobs content with filtering
   */
  async getJobs(options: {
    domain?: string;
    family?: string;
    mandant?: string;
    limit?: number;
    offset?: number;
    experienceLevel?: string;
    location?: string;
    salaryMin?: number;
    salaryMax?: number;
  } = {}): Promise<Job[]> {
    const params = new URLSearchParams();

    if (options.domain) params.append('domain', options.domain);
    if (options.family) params.append('family', options.family);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.experienceLevel) params.append('experience_level', options.experienceLevel);
    if (options.location) params.append('location', options.location);
    if (options.salaryMin) params.append('salary_min', options.salaryMin.toString());
    if (options.salaryMax) params.append('salary_max', options.salaryMax.toString());

    const url = `${this.baseUrl}/${getCompanyFromUrl()}/homepage/jobs${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Die Stellen konnten nicht geladen werden (Status ${response.status}).`);
    }

    const data = await response.json();
    return data.jobs || [];
  }

  /**
   * Test API connectivity
   */
  async testConnection(): Promise<{ message: string; timestamp: string; sections: string[] }> {
    const response = await fetch(`${this.baseUrl}/homepage/test`);
    
    if (!response.ok) {
      throw new Error(`Der Dienst für die Startseite ist nicht erreichbar (Status ${response.status}).`);
    }
    
    return response.json();
  }
}

export const homepageApi = new HomepageApiService();
